// A job brief in sections, as the creator app renders it: the idea, numbered
// must-haves, things to avoid, format chips, example videos, files, and the
// caption that goes under the post.
//
// Pure: no DOM, no fetch. The brand portal imports this module too, so a brief
// is shaped, cleaned and flattened the same way whoever wrote it. The Worker
// validates again on save; this is what the editors show and send.

import type {BriefSections} from '../../../shared/creator-api.ts';

export type {BriefSections};
export type BriefExample = BriefSections['examples'][number];
export type BriefFile = BriefSections['files'][number];

/** The Worker's own limits (domain/brief.ts), so nothing is cut silently on save. */
export const BRIEF_LIMITS = {
  idea: 4000,
  line: 240,
  must: 12,
  avoid: 12,
  format: 12,
  examples: 6,
  files: 12,
  caption: 1000,
} as const;

/** The chips most briefs need, offered as one-tap toggles. Any other chip can be typed. */
export const FORMAT_PRESETS = [
  '9:16',
  '15–30 s',
  '30–60 s',
  'Sound on',
  'Voiceover',
  'Face on camera',
  'Captions on screen',
  '#ad in caption',
];

export function emptyBrief(): BriefSections {
  return {idea: '', must: [], avoid: [], format: [], examples: [], files: [], caption: ''};
}

function strings(value: unknown, max: number): string[] {
  return Array.isArray(value) ? value.map((item) => String(item ?? '')).slice(0, max) : [];
}

/**
 * Sections back out of the plain text `briefText` writes, for a brief that was
 * only ever stored as text (a brand campaign saved before its sections were
 * kept). Null when the text has none of those headings: it is free text, and
 * reads as the idea.
 */
export function parseBriefText(text: string): BriefSections | null {
  const brief = emptyBrief();
  const idea: string[] = [];
  let found = false;
  let body = text.replace(/\r\n?/g, '\n');
  // The caption comes last and may have blank lines of its own: everything
  // after its heading is the caption.
  const caption = /(?:^|\n\n)Caption:\n/.exec(body);
  if (caption) {
    brief.caption = body.slice(caption.index + caption[0].length).trim();
    body = body.slice(0, caption.index);
    found = true;
  }
  for (const block of body.split(/\n{2,}/)) {
    const [head = '', ...rest] = block.split('\n');
    if (head.trim() === 'Must include:') {
      brief.must = rest.map((line) => line.replace(/^\s*\d+[.)]\s*/, '').trim()).filter(Boolean);
      found = true;
    } else if (head.trim() === 'Avoid:') {
      brief.avoid = rest.map((line) => line.replace(/^\s*[-•]\s*/, '').trim()).filter(Boolean);
      found = true;
    } else if (/^Format: /.test(head) && rest.length === 0) {
      brief.format = head.slice('Format: '.length).split(' · ').map((item) => item.trim()).filter(Boolean);
      found = true;
    } else if (head.trim() === 'Examples:') {
      brief.examples = rest.map((line) => line.trim()).filter(Boolean).map((url) => ({url}));
      found = true;
    } else if (/^Files: /.test(head) && rest.length === 0) {
      // The names alone cannot point at files again; the stored sections keep them.
      found = true;
    } else {
      idea.push(block);
    }
  }
  if (!found) return null;
  brief.idea = idea.join('\n\n').trim();
  return brief;
}

/**
 * Whatever the API holds, as sections: an object, the stored JSON text, or
 * nothing at all. A job written before briefs had sections has only its plain
 * `brief` text; that becomes the idea, as the migration does on the Worker,
 * unless it is text one of our editors wrote, which splits back into sections.
 */
export function readBrief(value: unknown, legacy = ''): BriefSections {
  let source = value;
  if (typeof source === 'string') {
    try {
      source = source.trim() ? JSON.parse(source) : null;
    } catch {
      source = null;
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return parseBriefText(legacy) ?? {...emptyBrief(), idea: legacy};
  }
  const raw = source as Record<string, unknown>;
  const examples = Array.isArray(raw.examples) ? raw.examples : [];
  const files = Array.isArray(raw.files) ? raw.files : [];
  return {
    idea: String(raw.idea ?? ''),
    must: strings(raw.must, BRIEF_LIMITS.must),
    avoid: strings(raw.avoid, BRIEF_LIMITS.avoid),
    format: strings(raw.format, BRIEF_LIMITS.format),
    examples: examples.slice(0, BRIEF_LIMITS.examples).map((item) => {
      const entry = (typeof item === 'string' ? {url: item} : item ?? {}) as Record<string, unknown>;
      const thumb = typeof entry.thumb_file_id === 'string' && entry.thumb_file_id ? {thumb_file_id: entry.thumb_file_id} : {};
      return {url: String(entry.url ?? ''), ...thumb};
    }),
    files: files.slice(0, BRIEF_LIMITS.files).map((item) => {
      const entry = (item ?? {}) as Record<string, unknown>;
      return {file_id: String(entry.file_id ?? ''), name: String(entry.name ?? '')};
    }).filter((file) => file.file_id),
    caption: String(raw.caption ?? ''),
  };
}

function lines(items: string[], max: number): string[] {
  const out: string[] = [];
  for (const item of items) {
    const text = item.replace(/\s+/g, ' ').trim().slice(0, BRIEF_LIMITS.line);
    if (text && !out.includes(text)) out.push(text);
  }
  return out.slice(0, max);
}

/** What gets saved: every line trimmed, empty ones and repeats dropped. */
export function cleanBrief(brief: BriefSections): BriefSections {
  return {
    idea: brief.idea.trim().slice(0, BRIEF_LIMITS.idea),
    must: lines(brief.must, BRIEF_LIMITS.must),
    avoid: lines(brief.avoid, BRIEF_LIMITS.avoid),
    format: lines(brief.format, BRIEF_LIMITS.format),
    examples: brief.examples
      .map((example) => ({...example, url: example.url.trim()}))
      .filter((example) => example.url)
      .slice(0, BRIEF_LIMITS.examples),
    files: brief.files.filter((file) => file.file_id).slice(0, BRIEF_LIMITS.files),
    caption: brief.caption.trim().slice(0, BRIEF_LIMITS.caption),
  };
}

export function briefIsEmpty(brief: BriefSections): boolean {
  const clean = cleanBrief(brief);
  return !clean.idea && !clean.must.length && !clean.avoid.length && !clean.format.length
    && !clean.examples.length && !clean.files.length && !clean.caption;
}

/** What a brief holds, in a few words for a summary: "Idea, 3 must-haves, caption"; empty when it holds nothing. */
export function briefSummary(brief: BriefSections): string {
  const clean = cleanBrief(brief);
  const many = (n: number, noun: string) => (n ? `${n} ${noun}${n === 1 ? '' : 's'}` : '');
  const text = [
    clean.idea ? 'idea' : '', many(clean.must.length, 'must-have'), clean.avoid.length ? `${clean.avoid.length} to avoid` : '',
    many(clean.examples.length, 'example'), many(clean.files.length, 'file'), clean.caption ? 'caption' : '',
  ].filter(Boolean).join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Whether two briefs say the same thing once cleaned, which is what bumps the version. */
export function sameBrief(left: BriefSections, right: BriefSections): boolean {
  return JSON.stringify(cleanBrief(left)) === JSON.stringify(cleanBrief(right));
}

/** An example link that is not https, or not a link at all. */
export function badExampleUrl(url: string): boolean {
  const text = url.trim();
  if (!text) return false;
  try {
    return new URL(text).protocol !== 'https:';
  } catch {
    return true;
  }
}

/** "tiktok.com" for a pasted link, for a tile that has no picture. */
export function exampleHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^(www|m|vm)\./, '');
  } catch {
    return 'link';
  }
}

/**
 * The sections as plain text, for every reader that only knows the old single
 * `brief` field: the web app, the emails, and app builds already in the stores.
 */
export function briefText(brief: BriefSections): string {
  const clean = cleanBrief(brief);
  const blocks: string[] = [];
  if (clean.idea) blocks.push(clean.idea);
  if (clean.must.length) blocks.push(['Must include:', ...clean.must.map((item, index) => `${index + 1}. ${item}`)].join('\n'));
  if (clean.avoid.length) blocks.push(['Avoid:', ...clean.avoid.map((item) => `- ${item}`)].join('\n'));
  if (clean.format.length) blocks.push(`Format: ${clean.format.join(' · ')}`);
  if (clean.examples.length) blocks.push(['Examples:', ...clean.examples.map((example) => example.url)].join('\n'));
  if (clean.files.length) blocks.push(`Files: ${clean.files.map((file) => file.name || 'file').join(', ')}`);
  if (clean.caption) blocks.push(`Caption:\n${clean.caption}`);
  return blocks.join('\n\n');
}
